/**
 * Icon Registry — maps semantic icon types to their stroke/fill SVG components
 * and default per-type colors (used in "color" icon mode).
 *
 * Icons are imported via vite-plugin-svgr (?react suffix).
 * All totodo-icons use currentColor; pikaicons already do (inbox).
 */
import type { FC, SVGProps } from "react"

// ---------------------------------------------------------------------------
// Sidebar icons — stroke
// ---------------------------------------------------------------------------
import InboxStroke from "@/shared/icons/pikaicons/stroke/communication/inbox-default.svg?react"
import TodayStroke from "@/shared/icons/totodoicons/stroke/star.svg?react"
import { DynamicUpcomingStroke } from "@/shared/icons/totodoicons/DynamicUpcomingIcon"
import AnydayStroke from "@/shared/icons/totodoicons/stroke/layers.svg?react"
import SomedayStroke from "@/shared/icons/totodoicons/stroke/archive.svg?react"
import LogbookStroke from "@/shared/icons/totodoicons/stroke/logbook.svg?react"
import TrashStroke from "@/shared/icons/totodoicons/stroke/delete.svg?react"
import SettingsStroke from "@/shared/icons/totodoicons/stroke/settings.svg?react"
import PlusStroke from "@/shared/icons/totodoicons/stroke/plus.svg?react"
import SearchStroke from "@/shared/icons/totodoicons/stroke/search.svg?react"
import TagStroke from "@/shared/icons/totodoicons/stroke/tag.svg?react"
import AreaStroke from "@/shared/icons/totodoicons/stroke/cube.svg?react"
import LaterProjectsStroke from "@/shared/icons/pikaicons/stroke/general/layout-grid-two-horizontal.svg?react"
import InvitesStroke from "@/shared/icons/pikaicons/stroke/alerts/notification-bell-on.svg?react"
import RecurringStroke from "@/shared/icons/pikaicons/stroke/arrows-chevrons/refresh.svg?react"
import RoutinesStroke from "@/shared/icons/pikaicons/stroke/time/reminder-clockwise.svg?react"

// ---------------------------------------------------------------------------
// Sidebar icons — solid (fill)
// ---------------------------------------------------------------------------
import InboxFill from "@/shared/icons/pikaicons/solid/communication/inbox-default.svg?react"
import TodayFill from "@/shared/icons/totodoicons/solid/star.svg?react"
import { DynamicUpcomingFill } from "@/shared/icons/totodoicons/DynamicUpcomingIcon"
import AnydayFill from "@/shared/icons/totodoicons/solid/layers.svg?react"
import SomedayFill from "@/shared/icons/totodoicons/solid/archive.svg?react"
import LogbookFill from "@/shared/icons/totodoicons/solid/logbook.svg?react"
import TrashFill from "@/shared/icons/totodoicons/solid/delete.svg?react"
import SettingsFill from "@/shared/icons/totodoicons/solid/settings.svg?react"
import TagFill from "@/shared/icons/totodoicons/solid/tag.svg?react"
import AreaFill from "@/shared/icons/totodoicons/solid/cube.svg?react"
import LaterProjectsFill from "@/shared/icons/pikaicons/solid/general/layout-grid-two-horizontal.svg?react"
import InvitesFill from "@/shared/icons/pikaicons/solid/alerts/notification-bell-on.svg?react"
import RecurringFill from "@/shared/icons/pikaicons/solid/arrows-chevrons/refresh.svg?react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type IconType =
  typeof SVG_COMPONENT_MAP extends Record<infer K, unknown> ? K : never

export type IconComponent = FC<SVGProps<SVGSVGElement>>

export interface IconEntry {
  stroke: IconComponent
  fill: IconComponent
  /** Per-type color used in "color" icon mode (not used when monochrome) */
  color: string
}

// ---------------------------------------------------------------------------
// Per-type colors — matches v1 palette
// ---------------------------------------------------------------------------

export const ICON_COLORS = {
  inbox: "#6895FF",
  today: "#F6B51E",
  upcoming: "#FF97A0",
  anyday: "#FFA469",
  someday: "#8C71F6",
  logbook: "#21C16B",
  trash: "#7B7B7B",
  settings: "#7B7B7B",
  plus: "#7B7B7B",
  search: "#7B7B7B",
  tags: "#7B7B7B",
  area: "var(--muted-foreground)",
  laterProjects: "#8C71F6",
  invites: "#8C71F6",
  recurring: "#21C16B",
  routines: "#8C71F6",
} as const

// ---------------------------------------------------------------------------
// Registry — Maps icon type → { stroke, fill, color }
// ---------------------------------------------------------------------------

const SVG_COMPONENT_MAP = {
  inbox: { stroke: InboxStroke, fill: InboxFill, color: ICON_COLORS.inbox },
  today: { stroke: TodayStroke, fill: TodayFill, color: ICON_COLORS.today },
  upcoming: {
    stroke: DynamicUpcomingStroke,
    fill: DynamicUpcomingFill,
    color: ICON_COLORS.upcoming,
  },
  anyday: { stroke: AnydayStroke, fill: AnydayFill, color: ICON_COLORS.anyday },
  someday: {
    stroke: SomedayStroke,
    fill: SomedayFill,
    color: ICON_COLORS.someday,
  },
  logbook: {
    stroke: LogbookStroke,
    fill: LogbookFill,
    color: ICON_COLORS.logbook,
  },
  trash: { stroke: TrashStroke, fill: TrashFill, color: ICON_COLORS.trash },
  settings: {
    stroke: SettingsStroke,
    fill: SettingsFill,
    color: ICON_COLORS.settings,
  },
  plus: { stroke: PlusStroke, fill: PlusStroke, color: ICON_COLORS.plus },
  search: {
    stroke: SearchStroke,
    fill: SearchStroke,
    color: ICON_COLORS.search,
  },
  tags: { stroke: TagStroke, fill: TagFill, color: ICON_COLORS.tags },
  area: { stroke: AreaStroke, fill: AreaFill, color: ICON_COLORS.area },
  laterProjects: {
    stroke: LaterProjectsStroke,
    fill: LaterProjectsFill,
    color: ICON_COLORS.laterProjects,
  },
  invites: {
    stroke: InvitesStroke,
    fill: InvitesFill,
    color: ICON_COLORS.invites,
  },
  recurring: {
    stroke: RecurringStroke,
    fill: RecurringFill,
    color: ICON_COLORS.recurring,
  },
  routines: {
    stroke: RoutinesStroke,
    fill: RoutinesStroke,
    color: ICON_COLORS.routines,
  },
} as const satisfies Record<string, IconEntry>

export type RegisteredIconType = keyof typeof SVG_COMPONENT_MAP

/**
 * Get the icon entry for a given type.
 * Returns `undefined` for unknown types instead of throwing.
 */
export function getIconEntry(type: RegisteredIconType): IconEntry {
  return SVG_COMPONENT_MAP[type]
}

/**
 * All registered icon types — useful for iteration/docs.
 */
export const ICON_TYPES = Object.keys(SVG_COMPONENT_MAP) as RegisteredIconType[]
